import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { usePendingBolEligibility } from "@/hooks/usePendingBolEligibility";
import type { OrderFormData } from "../orderFormTypes";

/**
 * "Pending BOL" — shared by the Create and Edit order dialogs so the
 * visibility rule and copy live in exactly one place.
 *
 * Renders only when the order is multi-leg and at least one leg's trailer is
 * physically empty. When checked, the BOL
 * requirement is waived at confirm time for everyone (no admin override). The
 * flag clears itself once a BOL is attached to the order.
 */
export function PendingBolCheckbox({
  formData,
  onChange,
  id = "pending-bol",
}: {
  formData: OrderFormData;
  onChange: (next: boolean) => void;
  id?: string;
}) {
  const { eligible } = usePendingBolEligibility(formData);

  const isMultiLeg = formData?.order_type === "multi-leg";
  if (!isMultiLeg) return null;

  const disabledReason = !eligible
      ? "Available once at least one leg is assigned a trailer with an empty load status."
      : null;
  const disabled = disabledReason !== null;

  return (
    <div
      className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3"
      title={disabledReason ?? undefined}
    >
      <Checkbox
        id={id}
        checked={!disabled && formData.pending_bol === true}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <div className="space-y-1">
        <Label
          htmlFor={id}
          className={`text-sm font-medium ${disabled ? "text-muted-foreground" : "cursor-pointer"}`}
        >
          Pending BOL
        </Label>
        <p className="text-xs text-muted-foreground">
          {disabledReason ??
            "At least one leg is moving an empty trailer. Confirm this order before the Bill of Lading exists — it clears automatically once a BOL is uploaded."}
        </p>
      </div>
    </div>
  );
}
